"use client";

/**
 * Offline operation queue ("outbox") for everything the widget sends on the
 * visitor's behalf.
 *
 * When the network is bad or the server is unreachable, an outbound call would
 * otherwise be swallowed (a send's `catch {}`, a fire-and-forget mutation) and
 * lost. The outbox persists un-acked operations so they survive a reload /
 * revisit and are retried automatically when connectivity returns.
 *
 * **Three semantics — one queue, different delivery guarantees:**
 *   - `queue`       — a sequence that must ALL be delivered, in order (chat
 *                     messages). Every op is distinct.
 *   - `convergence` — only the LATEST state per `convergenceKey` matters, order
 *                     + duplicates irrelevant (profile, preferences, a like
 *                     toggle, a read-ack). A new op SUPERSEDES a pending one
 *                     with the same key, so we never replay stale intermediate
 *                     states.
 *   - `beacon`      — best-effort telemetry that must not replay late. NOT
 *                     persisted to IndexedDB and dropped once `expiresAt`
 *                     passes. (Visitor tracking events - page views, link
 *                     and button events - currently use `queue`
 *                     instead: persisted, each stamped with its own
 *                     `occurredAt`, so a late delivery is still recorded at the
 *                     right time.)
 *
 * **Persistence: IndexedDB** (Blobs for Phase 2 media/voice ride the same op
 * model). The pure transforms below are I/O-free + unit-tested; the async IDB
 * read/write is wrapped so a failure degrades to an in-memory-only queue for
 * the session. Keyed per `publicKey` (the integration). Cross-tab writes fan
 * out via `BroadcastChannel`.
 */

export type OutboxStatus = "queued" | "sending" | "failed";
export type OpSemantics = "queue" | "convergence" | "beacon";

/** One un-acked outbound operation. `clientKey` is the stable identity across
 *  the whole lifecycle: optimistic-row id + React key (for messages), retry
 *  target, AND the idempotency key we can hand the BE so a retry after a lost
 *  ack doesn't duplicate. */
export interface OutboxOp {
  clientKey: string;
  /** Which sender handles this op — the shell's `run(op)` dispatches on it
   *  (e.g. "sendMessage", "setProfile", "setPreferences", "reaction",
   *  "readAck", "track"). */
  op: string;
  /** Delivery semantics — drives supersede + persistence (see file header). */
  semantics: OpSemantics;
  /** `convergence` ops sharing this key supersede each other (only the latest
   *  survives). Ignored for `queue` / `beacon`. */
  convergenceKey?: string;
  /** Op-specific payload (message text/threadKey, profile fields, reaction id,
   *  read-ack chatId, tracking event…). Cast by the matching sender. */
  payload: unknown;
  /** Epoch ms the op was created. Drives FIFO drain order. */
  createdAt: number;
  status: OutboxStatus;
  /** Count of failed attempts — drives exponential backoff. */
  attempts: number;
  /** Epoch ms before which the op must NOT be re-attempted (backoff after a
   *  failure). Absent / past → eligible now. Enforced across EVERY drain
   *  trigger so a failed op can't be hammered by a second trigger. */
  nextAttemptAt?: number;
  /** Epoch ms the op last became `sending`. A persisted `sending` op is only
   *  treated as abandoned (reset to `queued` on hydrate) once it is older than
   *  `SENDING_LEASE_MS`; absent (legacy record) counts as abandoned. */
  sendingSince?: number;
  /** `beacon` ops only: epoch ms after which the op is stale and dropped
   *  undelivered (a page-visit isn't worth replaying an hour later). */
  expiresAt?: number;
}

/**
 * Thrown by a `run` dispatcher when a BE call fails with a PERMANENT error —
 * one a retry can only reproduce (input validation, a 400 `BAD_USER_INPUT`).
 * The drain DROPS such an op (from the queue and, via `onDropped`, from the UI)
 * instead of hammering the BE with a request that can never succeed. A plain
 * throw (or any other error) stays transient and keeps retrying with backoff.
 */
/** How long a `sending` op may be in flight before another tab/session may take it over. Longer than any request. */
export const SENDING_LEASE_MS = 2 * 60_000;

export class PermanentOpError extends Error {
  constructor(message = "permanent outbox failure") {
    super(message);
    this.name = "PermanentOpError";
  }
}

const DB_NAME = "wexio-outbox";
const STORE = "queues";
const DB_VERSION = 1;

// ─── Pure queue transforms (no I/O — unit-tested directly) ──────────────────

/** Insert an op. Always a set-by-`clientKey`; for a `convergence` op also drop
 *  any pending op with the same `convergenceKey` (the new state supersedes the
 *  old — we never replay a stale intermediate profile/prefs/reaction/read). */
export function upsertOp(list: OutboxOp[], op: OutboxOp): OutboxOp[] {
  const without = list.filter((e) => {
    if (e.clientKey === op.clientKey) return false;
    if (
      op.semantics === "convergence" &&
      op.convergenceKey !== undefined &&
      e.semantics === "convergence" &&
      e.convergenceKey === op.convergenceKey
    ) {
      return false;
    }
    return true;
  });
  return [...without, op];
}

/** Patch one op's fields. No-op if absent. */
export function patchOp(
  list: OutboxOp[],
  clientKey: string,
  patch: Partial<Omit<OutboxOp, "clientKey">>,
): OutboxOp[] {
  return list.map((e) => (e.clientKey === clientKey ? { ...e, ...patch } : e));
}

/** Drop an op (acked, or the visitor discarded it). */
export function removeOp(list: OutboxOp[], clientKey: string): OutboxOp[] {
  return list.filter((e) => e.clientKey !== clientKey);
}

/** Remove stale `beacon` ops (expired before `now`) — they're best-effort and
 *  not worth delivering late. Returns the same array reference when nothing
 *  expired (so callers can skip a write). */
export function pruneExpired(list: OutboxOp[], now: number): OutboxOp[] {
  const kept = list.filter(
    (e) => !(e.semantics === "beacon" && e.expiresAt != null && e.expiresAt <= now),
  );
  return kept.length === list.length ? list : kept;
}

/** Ops eligible to (re)send right now, FIFO by `createdAt`. Excludes ones
 *  currently mid-flight (`sending`) so a slow request isn't double-fired. */
export function drainable(list: OutboxOp[]): OutboxOp[] {
  return list.filter((e) => e.status !== "sending").sort((a, b) => a.createdAt - b.createdAt);
}

/** Ops that get PERSISTED — everything but `beacon` (telemetry stays in memory
 *  so a stale event never survives a reload). */
export function persistable(list: OutboxOp[]): OutboxOp[] {
  return list.filter((e) => e.semantics !== "beacon");
}

/** Exponential backoff (ms) before the Nth retry — 2s, 4s, 8s … capped at 60s.
 *  `attempts` is the count of failures so far. */
export function backoffMs(attempts: number): number {
  return Math.min(60_000, 2_000 * 2 ** Math.max(0, attempts - 1));
}

function isOp(e: unknown): e is OutboxOp {
  return (
    !!e &&
    typeof e === "object" &&
    typeof (e as OutboxOp).clientKey === "string" &&
    typeof (e as OutboxOp).op === "string" &&
    typeof (e as OutboxOp).createdAt === "number"
  );
}

// ─── Persistence boundary ───────────────────────────────────────────────────
// The queue's durability is behind this interface so the store logic is
// storage-agnostic: production uses IndexedDB (below); tests inject an
// in-memory backend (`createMemoryPersistence`), keeping them hermetic without
// an IDB polyfill dependency. Both key the whole integration's queue by
// `publicKey`.
export interface OutboxPersistence {
  load(publicKey: string): Promise<OutboxOp[]>;
  save(publicKey: string, ops: OutboxOp[]): Promise<void>;
}

/** In-memory persistence — for tests + as the SSR/unsupported fallback. */
export function createMemoryPersistence(): OutboxPersistence {
  const map = new Map<string, OutboxOp[]>();
  return {
    load: (publicKey) => Promise.resolve((map.get(publicKey) ?? []).filter(isOp)),
    save: (publicKey, ops) => {
      if (ops.length === 0) map.delete(publicKey);
      else map.set(publicKey, ops);
      return Promise.resolve();
    },
  };
}

// ─── IndexedDB persistence (degrades to an in-memory-only queue) ────────────

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

/** Load the persisted queue for one integration. `[]` on any failure. Each
 *  integration's whole queue is one IDB record keyed by `publicKey`. */
export async function loadOutbox(publicKey: string): Promise<OutboxOp[]> {
  if (!publicKey) return [];
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(publicKey);
      req.onsuccess = () => {
        const val = req.result;
        resolve(Array.isArray(val) ? val.filter(isOp) : []);
      };
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

/** Persist (or, when empty, clear) one integration's queue. Resolves once the
 *  transaction settles; swallows failures (best-effort durability). The store
 *  already strips `beacon` ops before calling this (see `createOutboxStore`). */
export async function saveOutbox(publicKey: string, ops: OutboxOp[]): Promise<void> {
  if (!publicKey) return;
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      if (ops.length === 0) store.delete(publicKey);
      else store.put(ops, publicKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** IndexedDB-backed persistence — the production default. */
export const idbPersistence: OutboxPersistence = { load: loadOutbox, save: saveOutbox };

/** Pick the default persistence: IndexedDB where available, else an in-memory
 *  fallback (SSR / unsupported / blocked storage) so the queue still works for
 *  the session (it just won't survive a reload). */
function defaultPersistence(): OutboxPersistence {
  return typeof indexedDB === "undefined" ? createMemoryPersistence() : idbPersistence;
}

/**
 * A small live store around the persisted queue: holds the current ops in
 * memory, notifies subscribers synchronously on every change, persists to IDB
 * in the background (beacon ops excluded), and mirrors cross-tab writes via
 * `BroadcastChannel`.
 *
 * Loading is async, so `get()` returns `[]` until `ready` resolves; a `set()`
 * before then makes the in-memory queue authoritative (the pending load won't
 * clobber a fresh enqueue).
 */
export interface OutboxStore {
  get(): OutboxOp[];
  set(next: OutboxOp[]): void;
  subscribe(cb: (ops: OutboxOp[]) => void): () => void;
  /** Resolves once the initial IDB hydration has settled (or was superseded). */
  ready: Promise<void>;
  /** Tear down the cross-tab channel. */
  dispose(): void;
}

export function createOutboxStore(
  publicKey: string,
  persistence: OutboxPersistence = defaultPersistence(),
  opts: { crossTab?: boolean } = {},
): OutboxStore {
  let ops: OutboxOp[] = [];
  let mutated = false;
  const listeners = new Set<(ops: OutboxOp[]) => void>();
  const emit = () => {
    for (const cb of listeners) cb(ops);
  };

  const channel =
    (opts.crossTab ?? true) && typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel(`${DB_NAME}:${publicKey}`)
      : null;
  if (channel) {
    channel.onmessage = async () => {
      // Another tab wrote the queue — adopt its view so tabs don't fight.
      ops = await persistence.load(publicKey);
      emit();
    };
  }

  const ready = persistence.load(publicKey).then((loaded) => {
    // If an op was enqueued before hydration resolved, the in-memory queue is
    // authoritative — don't overwrite it with the (older) persisted snapshot.
    if (!mutated && loaded.length > 0) {
      // An op persisted mid-flight (tab closed during the request) would be
      // skipped by `drainable()` forever, so one whose lease expired is made
      // eligible again. A FRESH `sending` op may be live in another tab right
      // now (the BE does not dedup on clientKey), so it is left alone. Only the
      // initial load does this, not the cross-tab BroadcastChannel reload.
      const now = Date.now();
      ops = loaded.map((e) =>
        // A stamp in the future (wall clock moved back) can never expire, so it counts as abandoned.
        e.status === "sending" &&
        (e.sendingSince === undefined ||
          e.sendingSince > now ||
          now - e.sendingSince > SENDING_LEASE_MS)
          ? { ...e, status: "queued" as const }
          : e,
      );
      emit();
    }
  });

  return {
    get: () => ops,
    set: (next) => {
      mutated = true;
      ops = next;
      // In-memory view keeps everything (incl. beacon telemetry); only durable
      // ops are persisted so a stale beacon never survives a reload.
      void persistence.save(publicKey, persistable(next));
      channel?.postMessage("changed");
      emit();
    },
    subscribe: (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    ready,
    dispose: () => {
      listeners.clear();
      channel?.close();
    },
  };
}
